import {
  GiftSubscribeCardProps,
  ModalTriggerTextType,
} from "@bluebottlecoffee/design-system/components";
import { toHTML } from "@portabletext/to-html";
import useVariantPrice from "../chord/hooks/components/use-variant-price";
import { toImageProps } from "./image";
import { ConversionSchema } from "./conversion";
import { toVariantProps } from "./variant";
import { DereferencedSubscriptionOption } from "../sanity/product-queries";

/** The only properties required to build the modal trigger text */
type BuildModalTriggerTextProps = {
  data: ConversionSchema;
  lang: string;
  giftableSubscriptionOptions: DereferencedSubscriptionOption[];
  modalTriggerText: string;
};

/**
 * Concatenates the modal trigger text with the best value variant price.
 *
 * Uses the best value variant price if available, otherwise it finds the best
 * value variant and uses that price.
 */
function buildModalTriggerText({
  data,
  giftableSubscriptionOptions,
  modalTriggerText,
  lang,
}: BuildModalTriggerTextProps): ModalTriggerTextType {
  const {
    subscription: { durations, intervals },
  } = data;

  const variant = data.variants.find(
    (variant) => variant.sku === giftableSubscriptionOptions[0].item.sku,
  );

  const installmentCount = () => {
    const lowestDurationlength = Math.min(
      ...durations.map((duration) => duration.length),
    );

    const highestIntervalLength = Math.max(
      ...intervals.map((interval) => interval.length),
    );

    // Assuming the lowest duration length is in months & the highest interval
    // length is in weeks. This should be enforced in the CMS but is not currently
    switch (highestIntervalLength) {
      case 2:
        return lowestDurationlength * 2;
      case 3:
        return Math.floor(lowestDurationlength * (4 / 3));
      case 4:
        return lowestDurationlength;
      case 1:
      default:
        return lowestDurationlength * 4;
    }
  };

  return () => {
    const { discountPrice } = useVariantPrice({
      sku: giftableSubscriptionOptions[0].item.sku,
      quantity: giftableSubscriptionOptions[0].quantityLabel.quantity,
      intervalLength: intervals[0].length,
      intervalUnits: intervals[0].unit,
    });

    const toVariant = toVariantProps(variant, lang);

    return {
      activeText: modalTriggerText,
      quantity: 1,
      variant: {
        ...toVariant,
        regularPrice: {
          amount: toVariant.regularPrice.amount * installmentCount(),
          currency: toVariant.regularPrice.currency,
        },
        price: {
          amount: toVariant.price.amount * installmentCount(),
          currency: toVariant.price.currency,
        },
      },
      discountPrice: discountPrice && discountPrice * installmentCount(),
    };
  };
}

type GiftSubscribeTransformerProps = {
  data: ConversionSchema;
  includeReview?: boolean;
  lang: string;
  giftableSubscriptionOptions: DereferencedSubscriptionOption[];
  modalTriggerText: string;
};

export function toGiftSubscribeCardProps({
  data,
  includeReview = false,
  lang,
  giftableSubscriptionOptions,
  modalTriggerText,
}: GiftSubscribeTransformerProps): Omit<
  GiftSubscribeCardProps,
  "modalContent"
> {
  const reviewData = data.quickSubscribeCard?.product?.review;
  const reviewQuote = reviewData?.quote?.[lang];
  const reviewerName = reviewData?.reviewerName?.[lang];

  return {
    description: data.quickSubscribeCard.detail.recommendedCopy?.[lang]
      ? toHTML(data.quickSubscribeCard.detail.recommendedCopy[lang])
      : toHTML(data.descriptionV2[0]?.description?.[lang]),
    heading: data.name[lang],
    image: toImageProps(data.images[0], lang),
    modalTriggerText: buildModalTriggerText({
      data,
      lang,
      giftableSubscriptionOptions,
      modalTriggerText,
    }),
    notes: data.quickSubscribeCard.product.bottomCopy[lang],
    ...(includeReview &&
      reviewQuote &&
      reviewerName && {
        review: {
          author: reviewerName,
          quote: reviewQuote,
          rating: reviewData.rating ?? 5,
          ...(reviewData.verifiedBuyerLabel?.[lang] && {
            verifiedBuyerLabel: reviewData.verifiedBuyerLabel[lang],
          }),
        },
      }),
    subheading: toHTML(data.quickSubscribeCard.product.description[lang]),
  };
}
